import Map, { MapRef, Marker } from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import './MapComponent.scss';
import React, { useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { locationInstallationDetailPath } from '../../routes';

const DEFAULT_LATITUDE = 41.408404; // latitude of the La Salle Campus Barcelona
const DEFAULT_LONGITUDE = 2.1298879; // longitude of the La Salle Campus Barcelona
const ZOOM = 9; // zoom level

export interface MapData {
  latitude: number;
  longitude: number;
  location_id: string;
  color: string;
}

interface MapComponentProps {
  mapData?: MapData[];
}

const isValidCoordinate = (lat: number, lng: number) =>
  !isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180 && (lat !== 0 || lng !== 0);

const MapComponent = ({ mapData = [] }: MapComponentProps) => {
  const mapRef = useRef<MapRef>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const router = useNavigate();
  const currentPath = useLocation();

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const observer = new ResizeObserver(() => mapRef.current?.resize());
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  const validMapData = useMemo(() => mapData.filter((d) => isValidCoordinate(d.latitude, d.longitude)), [mapData]);

  const viewState = useMemo(() => {
    if (validMapData.length > 0) {
      const latestData = validMapData[0];
      return { latitude: latestData.latitude, longitude: latestData.longitude, zoom: ZOOM };
    }
    return { latitude: DEFAULT_LATITUDE, longitude: DEFAULT_LONGITUDE, zoom: ZOOM };
  }, [validMapData]);

  const goToLocation = (uuid: string) => {
    const segments = currentPath.pathname.split('/').filter(Boolean);
    const basePath = segments.length > 0 ? `/${segments[0]}` : '';
    router(`${basePath}/${locationInstallationDetailPath}/${uuid}`);
  };

  return (
    <div className="MapComponent" ref={containerRef}>
      <Map
        key={`${viewState.latitude},${viewState.longitude}`}
        ref={mapRef}
        initialViewState={viewState}
        style={{ width: '100%', height: '100%' }}
        mapStyle={'mapbox://styles/mapbox/streets-v11'}
        mapboxAccessToken={process.env.REACT_APP_MAPBOX_TOKEN}
        attributionControl={false}
      >
        {validMapData.map((data) => (
          <Marker
            className={'MapMarker'}
            key={data.location_id}
            longitude={data.longitude}
            latitude={data.latitude}
            color={data.color}
            onClick={() => {
              goToLocation(data.location_id);
            }}
          />
        ))}
      </Map>
    </div>
  );
};

export default MapComponent;
